import { MobXProviderContext } from 'mobx-react';
import type { SharedItemAuthor } from 'Modules/sharedItems/index.types';
import React from 'react';
import type { RootStoreProps } from 'Stores/RootStore.types';
import { formatNumberWithNationalCode } from 'Utils/phoneUtil';

export type SharedItemAuthorDisplay = {
  displayName: string;
  avatarUrl?: string;
  isInactive?: boolean;
};

type PersonStore = RootStoreProps['person'];

/**
 * Resolves an author's display fields using the person store.
 * This function only reads existing data and does not load missing person data.
 * Use {@link useSharedItemAuthor} when the person data may need to be loaded.
 */
export const resolveSharedItemAuthor = (
  author: SharedItemAuthor | null | undefined,
  personStore: PersonStore
): SharedItemAuthorDisplay => {
  if (author?.personId && author.personId > 0) {
    const personData = personStore.selectPersonValueById(author.personId);

    return {
      displayName: personData?.name || 'Unknown',
      avatarUrl: personData?.avatarUrl,
      isInactive: personData?.deleted,
    };
  }

  if (author?.phone) {
    const contact = personStore.getExtrContactByPhoneNumber(author.phone);

    return {
      displayName:
        contact?.name ||
        formatNumberWithNationalCode(author.phone) ||
        author.phone,
    };
  }

  return { displayName: 'Unknown' };
};

/**
 * Resolves an author's display fields and loads missing person data when needed.
 * Person data is loaded in an effect to avoid triggering side effects during render.
 */
export const useSharedItemAuthor = (
  author?: SharedItemAuthor | null
): SharedItemAuthorDisplay => {
  const { person } = React.useContext<RootStoreProps>(MobXProviderContext);

  React.useEffect(() => {
    if (author?.personId && author.personId > 0) {
      void person.loadPersonByIdGetIfMissingGet(author.personId.toString());
    }
  }, [author?.personId, person]);

  return resolveSharedItemAuthor(author, person);
};